import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components';
import { accountsApi } from '../../utils/accountsApi';
import './ChartOfAccountsDashboard.css';

const ChartOfAccountsDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        accountsApi.stats()
            .then(setStats)
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="chart-dashboard">
                <LoadingSpinner variant="fullpage" size="large" message="Loading Chart of Accounts…" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="chart-dashboard">
                <div className="error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    <p>Error: {error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="chart-dashboard">
            <div className="dashboard-header">
                <h1 className="dashboard-title">Chart of Accounts</h1>
                <p className="dashboard-subtitle">Overview and Management</p>
            </div>

            <div className="quick-actions">
                <div className="action-card primary" onClick={() => navigate('/chart-of-accounts/table')}>
                    <div className="action-icon"><i className="fas fa-table"></i></div>
                    <div className="action-content">
                        <h3>Table View</h3>
                        <p>View and manage accounts in a detailed table</p>
                    </div>
                    <i className="fas fa-arrow-right action-arrow"></i>
                </div>

                <div className="action-card secondary" onClick={() => navigate('/chart-of-accounts/card')}>
                    <div className="action-icon"><i className="fas fa-th-large"></i></div>
                    <div className="action-content">
                        <h3>Card View</h3>
                        <p>Browse accounts in an intuitive card layout</p>
                    </div>
                    <i className="fas fa-arrow-right action-arrow"></i>
                </div>

                <div className="action-card tertiary" onClick={() => navigate('/chart-of-accounts/tree')}>
                    <div className="action-icon"><i className="fas fa-sitemap"></i></div>
                    <div className="action-content">
                        <h3>Tree View</h3>
                        <p>Explore hierarchical account structure</p>
                    </div>
                    <i className="fas fa-arrow-right action-arrow"></i>
                </div>
            </div>

            {stats && (
                <>
                    <div className="statistics-grid">
                        <div className="stat-card blue">
                            <div className="stat-icon"><i className="fas fa-book"></i></div>
                            <div className="stat-content">
                                <h3>{stats.totalAccounts}</h3>
                                <p>Total Accounts</p>
                            </div>
                        </div>

                        <div className="stat-card green">
                            <div className="stat-icon"><i className="fas fa-layer-group"></i></div>
                            <div className="stat-content">
                                <h3>{stats.rootAccounts}</h3>
                                <p>Root Accounts</p>
                            </div>
                        </div>

                        <div className="stat-card orange">
                            <div className="stat-icon"><i className="fas fa-building"></i></div>
                            <div className="stat-content">
                                <h3>{stats.accountsWithCC}</h3>
                                <p>With Cost Center</p>
                            </div>
                        </div>

                        <div className="stat-card purple">
                            <div className="stat-icon"><i className="fas fa-file-invoice"></i></div>
                            <div className="stat-content">
                                <h3>{stats.postingAccounts}</h3>
                                <p>Posting Accounts</p>
                            </div>
                        </div>
                    </div>

                    <div className="details-section">
                        <div className="detail-card">
                            <h3 className="detail-title">
                                <i className="fas fa-chart-pie"></i> Account Types
                            </h3>
                            <div className="detail-content">
                                <div className="type-list">
                                    {[
                                        { label: 'Header (group)', count: stats.headerAccounts },
                                        { label: 'Posting (leaf)', count: stats.postingAccounts },
                                    ].map(({ label, count }) => (
                                        <div key={label} className="type-item">
                                            <span className="type-label">{label}</span>
                                            <div className="type-bar-container">
                                                <div
                                                    className="type-bar"
                                                    style={{
                                                        width: stats.totalAccounts
                                                            ? `${(count / stats.totalAccounts) * 100}%`
                                                            : '0%',
                                                    }}
                                                ></div>
                                            </div>
                                            <span className="type-count">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="detail-card">
                            <h3 className="detail-title">
                                <i className="fas fa-coins"></i> Accounts by Currency
                            </h3>
                            <div className="detail-content">
                                {stats.byCurrency && stats.byCurrency.length > 0 ? (
                                    <div className="currency-grid">
                                        {stats.byCurrency
                                            .sort((a, b) => b.count - a.count)
                                            .map(({ currencyCode, count }) => (
                                                <div key={currencyCode} className="currency-item">
                                                    <div className="currency-badge">{currencyCode}</div>
                                                    <div className="currency-count">{count} accounts</div>
                                                </div>
                                            ))}
                                    </div>
                                ) : (
                                    <p className="no-data">No accounts yet</p>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ChartOfAccountsDashboard;
